import React, { useEffect, useRef, useState } from "react";
import "../styles/HeroResumeGraphic.css";

export default function HeroResumeGraphic({
  fromScore = 62,
  toScore = 75,
  chipA = "PyTorch",
  chipB = "Kubernetes",
  titleName = "Jordan M.",
  titleRole = "AI Engineer",
}) {
  // --- Refs ---
  const heroImageRef = useRef(null);
  const ghostLayerRef = useRef(null);

  const resumeCardRef = useRef(null);
  const resumeBodyRef = useRef(null);
  const scanRef = useRef(null);

  // "Replaced line" refs (old/new)
  const replace1OldRef = useRef(null);
  const replace1NewRef = useRef(null);
  const replace2OldRef = useRef(null);
  const replace2NewRef = useRef(null);

  const chipARef = useRef(null);
  const chipBRef = useRef(null);

  const slot1Ref = useRef(null);
  const slot2Ref = useRef(null);

  const insertedARef = useRef(null);
  const insertedBRef = useRef(null);

  // --- State ---
  const [scoreFrom, setScoreFrom] = useState(fromScore);
  const [showBadge, setShowBadge] = useState(false);
  const [badgeDelta, setBadgeDelta] = useState(null); // <-- NEW: per-chip badge
  const [barPct, setBarPct] = useState(fromScore);
  const [isRewritten, setIsRewritten] = useState(false);

  // --- Internal refs for loop control ---
  const runningRef = useRef(false);
  const intervalRef = useRef(null);
  const ioRef = useRef(null);

  useEffect(() => {
    const hero = heroImageRef.current;
    const ghostLayer = ghostLayerRef.current;
    const resumeCard = resumeCardRef.current;
    const resumeBody = resumeBodyRef.current;
    const scanEl = scanRef.current;

    if (!hero || !ghostLayer || !resumeCard || !resumeBody || !scanEl) return;

    const prefersReduced =
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

    const cancelAllAnimations = (...els) => {
      els.flat().forEach((el) => {
        if (!el) return;
        if (el.getAnimations) el.getAnimations().forEach((a) => a.cancel());
      });
    };

    const rectRelativeToHero = (el) => {
      const r = el.getBoundingClientRect();
      const c = hero.getBoundingClientRect();
      return { x: r.left - c.left, y: r.top - c.top, w: r.width, h: r.height };
    };

    const clearGhosts = () => {
      ghostLayer.innerHTML = "";
    };

    const animateNumber = (from, to, durationMs) =>
      new Promise((resolve) => {
        const start = performance.now();
        const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

        const tick = (now) => {
          const t = Math.min(1, (now - start) / durationMs);
          const val = Math.round(from + (to - from) * easeOutCubic(t));
          setScoreFrom(val);
          if (t < 1) requestAnimationFrame(tick);
          else resolve();
        };

        requestAnimationFrame(tick);
      });

    const createGhostChip = (text, fromRect) => {
      const ghost = document.createElement("div");
      ghost.className = "heroChip heroGhostChip";
      ghost.textContent = text;
      ghost.style.left = `${fromRect.x}px`;
      ghost.style.top = `${fromRect.y}px`;
      ghost.style.width = `${fromRect.w}px`;
      ghost.style.height = `${fromRect.h}px`;
      ghostLayer.appendChild(ghost);
      return ghost;
    };

    const flyChip = async (chipEl, targetEl, revealEl) => {
      if (!chipEl || !targetEl || !revealEl) return;

      const from = rectRelativeToHero(chipEl);
      const to = rectRelativeToHero(targetEl);

      const dx = (to.x + 6) - from.x;
      const dy = (to.y + 1) - from.y;

      const ghost = createGhostChip(chipEl.textContent, from);

      chipEl.style.opacity = "0.45";
      chipEl.style.transition = "opacity .2s ease";

      const travel = ghost.animate(
        [
          { transform: "translate(0px, 0px) scale(1)", opacity: 1 },
          { transform: `translate(${dx}px, ${dy}px) scale(0.98)`, opacity: 1 },
        ],
        { duration: 700, easing: "cubic-bezier(.2,.8,.2,1)", fill: "forwards" }
      );
      await travel.finished;

      revealEl.style.opacity = "1";
      revealEl.style.transform = "translateY(0)";

      const fade = ghost.animate([{ opacity: 1 }, { opacity: 0 }], {
        duration: 180,
        easing: "ease-out",
        fill: "forwards",
      });
      await fade.finished;

      ghost.remove();
    };

    const flashBadge = async (delta) => {
      setBadgeDelta(delta);
      setShowBadge(true);
      await sleep(750);
      setShowBadge(false);
      await sleep(120);
      setBadgeDelta(null);
    };

    // -----------------------------
    // RESET
    // -----------------------------
    const resetState = () => {
      cancelAllAnimations(
        scanEl,
        replace1OldRef.current,
        replace1NewRef.current,
        replace2OldRef.current,
        replace2NewRef.current
      );

      // Reset rewrite visuals
      setIsRewritten(false);
      resumeCard.classList.remove("isRewritten");

      scanEl.style.opacity = "0";
      scanEl.style.transform = "translateY(-60px)";

      // Reset replaced lines
      if (replace1OldRef.current) {
        replace1OldRef.current.style.opacity = "1";
        replace1OldRef.current.style.transform = "translateY(0)";
      }
      if (replace1NewRef.current) {
        replace1NewRef.current.style.opacity = "0";
        replace1NewRef.current.style.transform = "translateY(1px)";
      }
      if (replace2OldRef.current) {
        replace2OldRef.current.style.opacity = "1";
        replace2OldRef.current.style.transform = "translateY(0)";
      }
      if (replace2NewRef.current) {
        replace2NewRef.current.style.opacity = "0";
        replace2NewRef.current.style.transform = "translateY(1px)";
      }

      // Restore chip opacity
      if (chipARef.current) chipARef.current.style.opacity = "1";
      if (chipBRef.current) chipBRef.current.style.opacity = "1";

      // Hide inserted keywords
      if (insertedARef.current) {
        insertedARef.current.style.opacity = "0";
        insertedARef.current.style.transform = "translateY(2px)";
      }
      if (insertedBRef.current) {
        insertedBRef.current.style.opacity = "0";
        insertedBRef.current.style.transform = "translateY(2px)";
      }

      setScoreFrom(fromScore);
      setBarPct(fromScore);
      setShowBadge(false);
      setBadgeDelta(null);
      clearGhosts();
    };

    const setFinalState = () => {
      // Reduced motion: show end state (rewritten + keywords + score)
      setIsRewritten(true);
      resumeCard.classList.add("isRewritten");

      if (replace1OldRef.current) replace1OldRef.current.style.opacity = "0";
      if (replace1NewRef.current) replace1NewRef.current.style.opacity = "1";
      if (replace2OldRef.current) replace2OldRef.current.style.opacity = "0";
      if (replace2NewRef.current) replace2NewRef.current.style.opacity = "1";

      if (insertedARef.current) {
        insertedARef.current.style.opacity = "1";
        insertedARef.current.style.transform = "translateY(0)";
      }
      if (insertedBRef.current) {
        insertedBRef.current.style.opacity = "1";
        insertedBRef.current.style.transform = "translateY(0)";
      }

      setScoreFrom(toScore);
      setBarPct(toScore);
      setBadgeDelta(toScore - fromScore);
      setShowBadge(true);
    };

    // -----------------------------
    // PART 1: REWRITE PASS (scan + line replace)
    // -----------------------------
    const runRewritePass = async () => {
      const doGlobalRewrite = () => {
        setIsRewritten(true);
        resumeCard.classList.add("isRewritten");
      };

      const replaceLine = async (oldEl, newEl) => {
        if (!oldEl || !newEl || !oldEl.animate || !newEl.animate) return;

        const out = oldEl.animate(
          [
            { opacity: 1, transform: "translateY(0px)" },
            { opacity: 0, transform: "translateY(-1px)" },
          ],
          { duration: 850, easing: "ease-out", fill: "forwards" }
        );

        const inn = newEl.animate(
          [
            { opacity: 0, transform: "translateY(1px)" },
            { opacity: 1, transform: "translateY(0px)" },
          ],
          {
            duration: 900,
            easing: "cubic-bezier(.2,.8,.2,1)",
            fill: "forwards",
          }
        );

        await Promise.allSettled([out.finished, inn.finished]);
      };

      const scanH = scanEl.offsetHeight || 46;
      const bodyH = resumeBody.offsetHeight || 360;
      const maxY = Math.max(0, bodyH - scanH - 6);

      scanEl.style.opacity = "1";
      scanEl.style.transform = `translateY(-${scanH}px)`;

      const scanAnim = scanEl.animate(
        [
          { transform: `translateY(-${scanH}px)`, opacity: 1 },
          { transform: `translateY(${maxY}px)`, opacity: 1 },
        ],
        { duration: 1600, easing: "cubic-bezier(.2,.8,.2,1)", fill: "forwards" }
      );

      await sleep(800);
      doGlobalRewrite();

      await Promise.all([
        replaceLine(replace1OldRef.current, replace1NewRef.current),
        replaceLine(replace2OldRef.current, replace2NewRef.current),
      ]);

      await scanAnim.finished;

      const fade = scanEl.animate([{ opacity: 1 }, { opacity: 0 }], {
        duration: 220,
        easing: "ease-out",
        fill: "forwards",
      });
      await fade.finished;
    };

    // -----------------------------
    // PART 2: KEYWORDS (UPDATED: each chip updates score)
    // -----------------------------
    const runKeywordsPass = async () => {
      const totalDelta = Math.max(0, toScore - fromScore);

      // Split delta across chips (A gets the bigger half if odd)
      const deltaA = Math.ceil(totalDelta / 2);
      const deltaB = totalDelta - deltaA;

      const midScore = fromScore + deltaA;

      // Chip A -> score to mid
      await flyChip(chipARef.current, slot1Ref.current, insertedARef.current);

      if (deltaA > 0) {
        setBarPct(midScore);
        await Promise.all([
          animateNumber(fromScore, midScore, 420),
          flashBadge(deltaA),
        ]);
      } else {
        await sleep(200);
      }

      await sleep(180);

      // Chip B -> score to final
      await flyChip(chipBRef.current, slot2Ref.current, insertedBRef.current);

      if (deltaB > 0) {
        setBarPct(toScore);
        await Promise.all([
          animateNumber(midScore, toScore, 420),
          flashBadge(deltaB),
        ]);
      } else {
        // still make bar snap to final in case of no delta
        setBarPct(toScore);
        await sleep(200);
      }
    };

    // -----------------------------
    // SINGLE LOOP: rewrite-first -> keywords -> score
    // -----------------------------
    const runOnce = async () => {
      if (runningRef.current) return;
      runningRef.current = true;

      resetState();
      await sleep(600);

      await runRewritePass();
      await sleep(350);

      await runKeywordsPass();

      // Hold final state a bit
      await sleep(900);

      runningRef.current = false;
    };

    const startLoop = () => {
      if (intervalRef.current) return;
      runOnce();
      intervalRef.current = setInterval(runOnce, 5500);
    };

    const stopLoop = () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      intervalRef.current = null;
    };

    if (prefersReduced) {
      setFinalState();
      return;
    }

    ioRef.current = new IntersectionObserver(
      (entries) => {
        const e = entries[0];
        if (!e) return;
        if (e.isIntersecting) startLoop();
        else stopLoop();
      },
      { threshold: 0.25 }
    );

    ioRef.current.observe(hero);

    return () => {
      stopLoop();
      if (ioRef.current) ioRef.current.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromScore, toScore, chipA, chipB]);

  return (
    <div className="hero-image" ref={heroImageRef}>
      <div className="heroGraphic" aria-hidden="true">
        {/* Left: Resume */}
        <div
          className={`heroCard resumeCard ${isRewritten ? "isRewritten" : ""}`}
          ref={resumeCardRef}
        >
          <div className="cardTop cardTopRow">
            <span>Resume Preview</span>
            <span className={`rewriteBadge ${isRewritten ? "isOn" : ""}`}>
              {isRewritten ? "Rewritten" : "Original"}
            </span>
          </div>

          <div className="resumeBody" ref={resumeBodyRef}>
            <div className="rewriteScan" ref={scanRef} />

            <div className="resumeName">{titleName}</div>
            <div className="resumeTitle">{titleRole}</div>

            <div className="resumeBlock">
              <div className="resumeHeading">Professional Summary</div>

              <div className="resumeLineGroup w90">
                <div className="resumeLine resumeLineOld" ref={replace1OldRef} />
                <div className="resumeLine resumeLineNew" ref={replace1NewRef} />
              </div>

              <div className="resumeLine w82" />
              <div className="resumeLine w74" />
            </div>

            <div className="resumeBlock">
              <div className="resumeHeading">Work Experience</div>

              <div className="resumeLine w88" />

              <div className="resumeLineGroup w80">
                <div className="resumeLine resumeLineOld" ref={replace2OldRef} />
                <div className="resumeLine resumeLineNew" ref={replace2NewRef} />
              </div>

              <div className="resumeLine w76" />
              <div className="resumeLine w84" />
            </div>

            <div className="resumeBlock">
              <div className="resumeHeading">Education</div>
              <div className="resumeLine w72" />
              <div className="resumeLine w60" />
            </div>

            <div className="resumeBlock">
              <div className="resumeHeading">Skills</div>
              <div className="resumeLine w86" />
              <div className="resumeLine w78" />

              <div className="resumeKeywordsRow">
                <span className="resumeKeywordsLabel">Keywords:</span>

                <span className="resumeInsertSlot" ref={slot1Ref}>
                  <span
                    className="insertedKeyword"
                    ref={insertedARef}
                    style={{ opacity: 0, transform: "translateY(2px)" }}
                  >
                    {chipA}
                  </span>
                </span>

                <span className="resumeInsertSlot" ref={slot2Ref}>
                  <span
                    className="insertedKeyword"
                    ref={insertedBRef}
                    style={{ opacity: 0, transform: "translateY(2px)" }}
                  >
                    {chipB}
                  </span>
                </span>

                <span className="resumeStaticKeywordExists">Python</span>
                <span className="resumeStaticKeyword">LLMs</span>
              </div>
            </div>

            <div className="resumeNote">
              Market-trained rewrite + keyword coverage.
            </div>
          </div>
        </div>

        {/* Right: Gap panel */}
        <div className="heroCard gapCard">
          <div className="gapHeader">
            <div>
              <div className="gapTitle">Missing keywords</div>
              <div className="gapSubtitle">
                Add these to maximize your chances of getting an interview.
              </div>
            </div>

            <div className="scoreWrap">
              <div className="scoreLabel">Keyword Score</div>
              <div className="scoreRow">
                <span className="scoreFrom">{scoreFrom}</span>
                <span className="scoreArrow">→</span>
                <span className="scoreTo">{toScore}</span>
                <span className={`scoreBadge ${showBadge ? "isVisible" : ""}`}>
                  +{badgeDelta ?? (toScore - fromScore)}
                </span>
              </div>

              <div className="scoreBar">
                <div className="scoreBarFill" style={{ width: `${barPct}%` }} />
              </div>
            </div>
          </div>

          <div className="gapSection">
            <div className="gapSectionTitle">Programming Languages</div>
            <div className="chipRow">
              <span className="heroChipExists">Python</span>
              <span className="heroChip">TypeScript</span>
              <span className="heroChip">SQL</span>
            </div>
          </div>

          <div className="gapSection">
            <div className="gapSectionTitle">Applications</div>
            <div className="chipRow">
              <span className="heroChip" ref={chipARef}>
                {chipA}
              </span>
              <span className="heroChip" ref={chipBRef}>
                {chipB}
              </span>
              <span className="heroChip">Docker</span>
            </div>
          </div>

          <div className="gapSection">
            <div className="gapSectionTitle">Technical Skills</div>
            <div className="chipRow">
              <span className="heroChip">MLOps</span>
              <span className="heroChip">Vector Databases</span>
            </div>
          </div>
        </div>

        {/* Ghost chips */}
        <div className="ghostLayer" ref={ghostLayerRef} />
      </div>
    </div>
  );
}
